import { findNodeById } from '../hooks/useConfigTree'
import { getModuleDraft, markModuleDraftClean } from '../store/moduleDraftStore'
import type { ConfigNode } from '../types/config.types'
import type { WorkspaceTab } from '../types/editor.types'
import { codeService } from './codeService'
import { moduleIdFromTabId } from './objectModuleFlush'

export type ResolvableCommonModule = {
  tabId: string
  moduleId: string
  nodeId: string
}

/** Valid Python-identifier names of commonModule nodes (for completions). */
export function listCommonModuleNames(tree: ConfigNode[]): string[] {
  const names: string[] = []
  const walk = (nodes: ConfigNode[]) => {
    for (const n of nodes) {
      if (n.type === 'commonModule' && n.name && /^[A-Za-z_][A-Za-z0-9_]*$/.test(n.name)) {
        names.push(n.name)
      }
      if (n.children?.length) walk(n.children)
    }
  }
  walk(tree)
  return names
}

export function resolveCommonModuleTarget(
  tab: Pick<WorkspaceTab, 'id' | 'editorType' | 'nodeId' | 'nodeType' | 'isDirty'>,
  tree: ConfigNode[],
): ResolvableCommonModule | null {
  if (tab.editorType !== 'code' || !tab.isDirty) return null
  const node = findNodeById(tree, tab.nodeId)
  if (!node) return null
  const isCommon =
    tab.nodeType === 'commonModule' || node.type === 'commonModule'
  if (!isCommon) return null
  return {
    tabId: tab.id,
    moduleId: moduleIdFromTabId(tab.id),
    nodeId: tab.nodeId,
  }
}

export async function flushCommonModuleCode(args: {
  nodeId: string
  moduleId: string
  code: string
  updateNodePatch: (nodeId: string, patch: Partial<ConfigNode>) => void
}): Promise<void> {
  const { nodeId, moduleId, code, updateNodePatch } = args
  updateNodePatch(nodeId, { module_code: code })
  await codeService.saveModule(moduleId, code)
  markModuleDraftClean(moduleId, code)
}

export async function flushDirtyCommonModules(args: {
  tabs: WorkspaceTab[]
  tree: ConfigNode[]
  updateNodePatch: (nodeId: string, patch: Partial<ConfigNode>) => void
}): Promise<{ flushedTabIds: string[] }> {
  const { tabs, tree, updateNodePatch } = args
  const flushedTabIds: string[] = []
  for (const tab of tabs) {
    const target = resolveCommonModuleTarget(tab, tree)
    if (!target) continue
    const code = getModuleDraft(target.moduleId)?.code ?? ''
    const node = findNodeById(tree, target.nodeId)
    const moduleName = node?.name ?? target.nodeId
    try {
      await flushCommonModuleCode({
        nodeId: target.nodeId,
        moduleId: target.moduleId,
        code,
        updateNodePatch,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      throw new Error(`Failed to save common module ${moduleName}: ${message}`)
    }
    flushedTabIds.push(target.tabId)
  }
  return { flushedTabIds }
}
